import { useTranslation } from 'react-i18next';
import { X, FileText, CheckCircle, XCircle, AlertTriangle, ArrowRight } from 'lucide-react';
import type { PurchaseRequest } from '../../../api/queries/purchasing.queries';
import { useAuthStore } from '../../../stores/auth.store';

interface PRDetailModalProps {
  pr: PurchaseRequest;
  onClose: () => void;
  onConvertToPO: () => void;
  onApprove: () => void;
  onReject: () => void;
  onRefresh: () => void;
}

export default function PRDetailModal({ pr, onClose, onConvertToPO, onApprove, onReject }: PRDetailModalProps) {
  const { t, i18n } = useTranslation();
  const { user } = useAuthStore();

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString(i18n.language === 'th' ? 'th-TH' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'approved':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'rejected':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'converted':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'text-red-600 bg-red-50';
      case 'high':
        return 'text-orange-600 bg-orange-50';
      default:
        return 'text-blue-600 bg-blue-50';
    }
  };

  const canConvertToPO = (user?.role === 'HQ_PURCHASING' || user?.role === 'HQ_ADMIN') && pr.status === 'approved';
  const canApprovePR = (user?.role === 'HQ_PURCHASING' || user?.role === 'HQ_ADMIN') && pr.status === 'pending';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="relative w-full max-w-2xl max-h-[90vh] overflow-hidden rounded-lg bg-card flex flex-col">
        {/* Header */}
        <div className="border-b p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText className="h-5 w-5 text-primary" />
            <div>
              <h2 className="text-xl font-bold">{pr.prNumber}</h2>
              <p className="text-sm text-muted-foreground">{pr.branchId.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-2 py-1 rounded border text-xs ${getStatusColor(pr.status)}`}>
              {t(`purchasing.${pr.status}`)}
            </span>
            <span className={`px-2 py-1 rounded text-xs ${getPriorityColor(pr.urgency)}`}>
              {t(`purchasing.${pr.urgency}`)}
            </span>
            <button onClick={onClose} className="p-2 hover:bg-secondary rounded ml-2">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Status Timeline */}
            <div className="flex items-center gap-4 p-4 rounded-lg bg-secondary/50">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-medium">{t('purchasing.created')}</p>
                  <p className="text-xs text-muted-foreground">{formatDate(pr.createdAt)}</p>
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
              {pr.status === 'approved' && pr.approvedBy && (
                <>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">{t('purchasing.approved')}</p>
                      <p className="text-xs text-muted-foreground">
                        {pr.approvedBy.username} • {pr.approvedAt && formatDate(pr.approvedAt)}
                      </p>
                    </div>
                  </div>
                </>
              )}
              {pr.status === 'rejected' && pr.rejectedBy && (
                <>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                      <XCircle className="h-4 w-4 text-red-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">{t('purchasing.rejected')}</p>
                      <p className="text-xs text-muted-foreground">{pr.rejectedBy.username}</p>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Rejection Reason */}
            {pr.rejectionReason && (
              <div className="p-4 rounded-lg bg-red-50 border border-red-200">
                <p className="text-sm font-medium text-red-800">{t('purchasing.rejectionReason')}:</p>
                <p className="text-sm text-red-700 mt-1">{pr.rejectionReason}</p>
              </div>
            )}

            {/* Urgency Reason */}
            {pr.urgency !== 'normal' && pr.urgencyReason && (
              <div className="p-4 rounded-lg bg-orange-50 border border-orange-200">
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle className="h-4 w-4 text-orange-600" />
                  <p className="text-sm font-medium text-orange-800">{t('purchasing.urgencyReason')}:</p>
                </div>
                <p className="text-sm text-orange-700">{pr.urgencyReason}</p>
              </div>
            )}

            {/* Request Info */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">{t('purchasing.requestedBy')}</p>
                <p className="font-medium">{pr.requestedBy.username}</p>
              </div>
              {pr.requestedDeliveryDate && (
                <div>
                  <p className="text-muted-foreground">{t('purchasing.requestedDelivery')}</p>
                  <p className="font-medium">{formatDate(pr.requestedDeliveryDate)}</p>
                </div>
              )}
            </div>

            {/* Items Table */}
            <div>
              <h3 className="text-sm font-medium mb-3">{t('purchasing.items')}</h3>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/50">
                    <tr>
                      <th className="text-left py-2 px-3">{t('purchasing.item')}</th>
                      <th className="text-right py-2 px-3">{t('purchasing.qty')}</th>
                      <th className="text-right py-2 px-3">{t('purchasing.unit')}</th>
                      <th className="text-right py-2 px-3">{t('purchasing.estPrice')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pr.items.map((item) => (
                      <tr key={item._id} className="border-t">
                        <td className="py-2 px-3">
                          <div className="flex items-center gap-2">
                            {item.isFreeFormItem ? (
                              <>
                                <span>{item.customItemName}</span>
                                <span className="text-xs px-1 py-0.5 rounded bg-yellow-100 text-yellow-700">
                                  {t('purchasing.custom')}
                                </span>
                              </>
                            ) : (
                              <span>{item.ingredientId?.name}</span>
                            )}
                          </div>
                          {item.customItemDescription && (
                            <p className="text-xs text-muted-foreground mt-0.5">{item.customItemDescription}</p>
                          )}
                        </td>
                        <td className="py-2 px-3 text-right">
                          {item.quantity}
                          {item.originalQuantity && item.originalQuantity !== item.quantity && (
                            <span className="text-xs text-muted-foreground ml-1">
                              ({t('purchasing.was')} {item.originalQuantity})
                            </span>
                          )}
                        </td>
                        <td className="py-2 px-3 text-right">{item.unit}</td>
                        <td className="py-2 px-3 text-right">
                          {item.estimatedPrice ? formatCurrency(item.estimatedPrice * item.quantity) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-secondary/50">
                    <tr>
                      <td colSpan={3} className="py-2 px-3 text-right font-medium">
                        {t('purchasing.total')}:
                      </td>
                      <td className="py-2 px-3 text-right font-bold">
                        {formatCurrency(pr.totalEstimatedCost || 0)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* HQ Modifications */}
            {pr.wasModifiedByHQ && pr.hqModificationNotes && (
              <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                <p className="text-sm font-medium text-blue-800">{t('purchasing.hqModifications')}:</p>
                <p className="text-sm text-blue-700 mt-1">{pr.hqModificationNotes}</p>
              </div>
            )}

            {/* Notes */}
            {pr.notes && (
              <div>
                <h3 className="text-sm font-medium mb-2">{t('common.notes')}</h3>
                <p className="text-sm text-muted-foreground">{pr.notes}</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t p-4 flex justify-between">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border hover:bg-secondary transition-colors"
          >
            {t('common.close')}
          </button>
          <div className="flex gap-2">
            {canApprovePR && (
              <>
                <button
                  onClick={onReject}
                  className="px-4 py-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2"
                >
                  <XCircle className="h-4 w-4" />
                  {t('purchasing.rejectPR')}
                </button>
                <button
                  onClick={onApprove}
                  className="px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors flex items-center gap-2"
                >
                  <CheckCircle className="h-4 w-4" />
                  {t('purchasing.approvePR')}
                </button>
              </>
            )}
            {canConvertToPO && (
              <button
                onClick={onConvertToPO}
                className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                {t('purchasing.convertToPO')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
